<template>
  <!-- 模板部分保持不变 -->
  <div class="glb-container">
    <!-- Three.js渲染容器 -->
    <div id="threeContainer" class="three-container"></div>

    <!-- 模型预览图 -->
    <div class="three-image" v-if="url && loadProgress < 100">
      <img :src="url"/>
    </div>
    <!-- <div class="fix-camera">
      <a-slider v-model:value="cameraPosition.x" :max="360" :min="-360" placeholder="输入x" @change="resetCameraSize(cameraPosition.x,cameraPosition.y,cameraPosition.z)"></a-slider>
      <a-slider v-model:value="cameraPosition.y" :max="360" :min="-360" placeholder="输入y" @change="resetCameraSize(cameraPosition.x,cameraPosition.y,cameraPosition.z)"></a-slider>
      <a-slider v-model:value="cameraPosition.z" :max="360" :min="-360" placeholder="输入z" @change="resetCameraSize(cameraPosition.x,cameraPosition.y,cameraPosition.z)"></a-slider>
    </div> -->
    <!-- 模型加载进度条 -->
    <div v-if="loadProgress < 100" class="load-progress">
      <div class="progress-content">
        <div class="progress-text">{{ getProgressText() }}</div>
        <div class="progress-bar-container" v-if="loadProgress > 0">
          <div class="progress-bar" :style="{ width: loadProgress + '%' }"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, reactive, defineExpose, defineProps, defineEmits, nextTick } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js'
import TWEEN from '@tweenjs/tween.js'
import axios from 'axios'
import JSZip from 'jszip'
import { saveModelToIndexedDB, getModelFromIndexedDB, cleanupIndexedDBCache } from '/@/utils/IndexedDB'
import { initThree } from '/@/utils/threeHelper'
import GeoTIFF, { fromArrayBuffer } from 'geotiff'

// 接收父组件传递的props
const props = defineProps({
  modelList: {
    type: Object,
    default: () => ({})
  },
  currentModelKey: {
    type: String,
    default: ''
  },
  currentModelIndex: {
    type: Number,
    default: 0
  }
})

// 定义事件
const emit = defineEmits(['model-selected'])

// 响应式数据
const loadProgress = ref(0)
const url = ref('')
const modelInfo = reactive({
  visible: false,
  size: '0 x 0 x 0',
  vertexCount: 0,
  faceCount: 0
})

// 全局变量
let scene, camera, renderer, controls, model
let animationId = null

// 添加加载控制变量
let currentLoadController = null // 当前加载的AbortController
const currentGLTFLoader = null // 当前GLTFLoader实例
let isLoading = false // 是否正在加载
let currentLoadId = 0 // 当前加载的ID，用于防止取消后仍执行回调
let blobUrls = [] // 存储创建的Blob URL

const loadStatus = ref('准备下载...')
const loadError = ref(false)
const errorMessage = ref('')

const cameraPosition = reactive({
  x: 25,
  y: 50,
  z: 10
})
// 获取进度文本
function getProgressText () {
  if (loadProgress.value === 0) {
    return loadStatus.value
  } else if (loadProgress.value < 100) {
    return `${loadStatus.value} ${loadProgress.value}%`
  }
  return '加载完成'
}

onMounted(() => {
  initializeScene()
})

// 在组件销毁时清理资源
onUnmounted(() => {
  window.removeEventListener('resize', updateRendererSize)
  cleanupResources()
})

/**
 * 清理Blob URLs
 */
function cleanupBlobUrls () {
  if (blobUrls && blobUrls.length > 0) {
    blobUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        console.warn('清理Blob URL失败:', e)
      }
    })
    blobUrls = []
  }
}

/**
 * 取消当前加载的模型
 */
async function cancelCurrentLoad () {
  if (!isLoading) return

  console.log('开始取消当前加载...')

  try {
    // 1. 取消HTTP请求
    if (currentLoadController) {
      currentLoadController.abort()
      currentLoadController = null
      console.log('HTTP请求已取消')
    }

    // 2. 增加加载ID，使旧的加载回调不会执行
    currentLoadId++

    // 3. 清理Blob URLs
    cleanupBlobUrls()

    // 4. 停止动画混合器
    if (window.animationMixer) {
      window.animationMixer.stopAllAction()
      window.animationMixer = null
      console.log('动画混合器已停止')
    }

    // 5. 移除当前模型（但保留场景）
    removeCurrentModel()

    // 6. 重置状态
    isLoading = false

    console.log('当前加载已取消')
  } catch (error) {
    console.warn('取消加载时出错:', error)
  }
}

/**
 * 加载选中的模型
 */
async function loadSelectedModel (row) {
  console.log('开始加载模型:', row.modelName || '未命名模型')

  // 等待DOM更新完成
  await nextTick()

  // 强制更新渲染器尺寸
  updateRendererSize()

  // 彻底清理场景中的模型
  await cleanupSceneModels()

  // 设置加载ID，用于标识当前加载
  const loadId = ++currentLoadId
  isLoading = true

  // 设置预览图
  url.value = row.thumbnailUrl || ''

  // 重置加载进度
  loadProgress.value = 1
  loadStatus.value = '开始加载...'

  // 开始加载新模型
  try {
    await loadModelFromData(row, loadId)
  } catch (error) {
    // 如果是当前加载，才显示错误
    if (loadId === currentLoadId) {
      console.error('加载模型失败:', error)
      loadProgress.value = 0
      loadStatus.value = '加载失败'
      isLoading = false
    }
  }
}

function cleanupSceneModels () {
  return new Promise((resolve) => {
    // 停止所有动画
    if (window.animationMixer) {
      window.animationMixer.stopAllAction()
      window.animationMixer = null
    }

    // 遍历场景，移除所有网格对象
    if (scene) {
      const objectsToRemove = []

      scene.traverse((object) => {
        // 移除所有网格对象（包括组）
        if (object.isMesh || object.isGroup || object.isObject3D) {
          // 排除灯光和相机
          if (!object.isLight && !object.isCamera) {
            objectsToRemove.push(object)
          }
        }
      })

      // 从场景中移除找到的对象
      objectsToRemove.forEach((object) => {
        // 如果对象有父级，从父级中移除
        if (object.parent) {
          object.parent.remove(object)
        } else {
          // 否则直接从场景中移除
          scene.remove(object)
        }

        // 清理资源
        disposeObject(object)
      })

      // 重置模型引用
      model = null
    }

    // 清理Blob URLs
    cleanupBlobUrls()

    console.log('场景清理完成')
    resolve()
  })
}

function removeCurrentModel () {
  if (model) {
    // 递归移除模型及其所有子对象
    const removeObject = (object) => {
      if (object.parent) {
        object.parent.remove(object)
      } else {
        scene.remove(object)
      }

      // 清理资源
      disposeObject(object)

      // 递归处理子对象
      if (object.children && object.children.length > 0) {
        const children = [...object.children] // 创建副本，因为遍历时会修改原数组
        children.forEach(child => removeObject(child))
      }
    }

    removeObject(model)
    model = null

    console.log('模型已完全从场景移除')
  }
}

// 新增：更新渲染器尺寸
function updateRendererSize () {
  if (!renderer || !camera) return
  const container = document.getElementById('threeContainer')
  if (!container) return

  const width = container.clientWidth
  const height = container.clientHeight

  if (width === 0 || height === 0) {
    console.warn('Container size is 0, skip resize')
    return
  }

  // 更新渲染器
  renderer.setSize(width, height)
  // 更新相机
  camera.aspect = width / height
  camera.updateProjectionMatrix()
}

/**
 * 初始化场景
 */
async function initializeScene () {
  const container = document.getElementById('threeContainer')
  if (!container) {
    console.error('容器元素未找到')
    return
  }

  try {
    const threeInstance = initThree(container)
    scene = threeInstance.scene
    camera = threeInstance.camera
    renderer = threeInstance.renderer
    controls = threeInstance.controls

    updateRendererSize()

    // 优化渲染器设置
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.0

    // 设置输出编码（兼容不同版本）
    if (renderer.outputEncoding !== undefined) {
      renderer.outputEncoding = THREE.sRGBEncoding
    } else if (renderer.outputColorSpace !== undefined) {
      renderer.outputColorSpace = THREE.SRGBColorSpace
    }

    // 添加灯光
    setupLights()

    // 启动渲染循环
    startRenderLoop()

    console.log('Three.js场景初始化完成')
  } catch (error) {
    console.error('初始化Three.js场景失败:', error)
  }
}

async function loadModelFromData (row, loadId) {
  // 检查是否是当前加载
  if (loadId !== currentLoadId) {
    console.log('加载已取消，跳过:', loadId)
    return
  }

  try {
    if (!row || !row.modelUrl) {
      console.error('模型数据无效或缺少URL')
      loadStatus.value = '模型数据无效'
      isLoading = false
      return
    }

    const fileUrl = row.modelUrl
    const fileType = (row.fileType || row.model3dType || '').toLowerCase()
    const localStorageKey = `${props.currentModelKey}-${props.currentModelIndex}`
    // 支持的文件格式检查
    const supportedFormats = ['obj', 'gltf', 'glb', 'tif', 'tiff', 'ply']
    if (row.fileType && !supportedFormats.includes(fileType)) {
      console.error(`当前仅支持OBJ、GLTF、GLB、TIFF、PLY格式预览，不支持 ${fileType}`)
      loadStatus.value = `不支持 ${fileType} 格式`
      isLoading = false
      return
    }

    // 重置进度
    loadProgress.value = 1
    loadStatus.value = '开始下载...'

    // 特殊处理 TIFF 文件 - 直接下载，不通过 ZIP 解压
    if (fileType === 'tif' || fileType === 'tiff') {
      await loadTIFFFromUrl(fileUrl, loadId, row)
      return
    }

    // 其他格式（GLTF/GLB/OBJ）使用原有的 ZIP 加载逻辑
    let zipBlob
    let zipContent
    let fromCache = false

    // 尝试从 IndexedDB 缓存加载
    try {
      console.log('尝试从 IndexedDB 缓存加载:', localStorageKey)
      const cachedBlob = await getModelFromIndexedDB(localStorageKey)

      if (cachedBlob) {
        zipBlob = cachedBlob

        // 加载 ZIP
        const zip = new JSZip()
        zipContent = await zip.loadAsync(zipBlob)

        loadProgress.value = 50
        loadStatus.value = '从缓存加载模型...'
        fromCache = true

        console.log('IndexedDB 缓存加载成功:', localStorageKey)
      }
    } catch (cacheError) {
      console.warn('从 IndexedDB 缓存加载失败，将重新下载:', cacheError)
      fromCache = false
    }

    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('加载已取消，跳过下载')
      return
    }

    // 如果没有从缓存成功加载，则重新下载
    if (!fromCache || !zipContent) {
      console.log('重新下载模型:', localStorageKey)

      try {
        // 创建AbortController用于取消下载
        currentLoadController = new AbortController()

        zipBlob = await downloadFileAsBlob(fileUrl, currentLoadController.signal)

        // 检查是否是当前加载
        if (loadId !== currentLoadId) {
          console.log('下载后检查，加载已取消')
          return
        }

        loadProgress.value = 30
        loadStatus.value = '文件下载完成，开始解压...'
        console.log('下载完成，文件大小:', formatFileSize(zipBlob.size))
      } catch (downloadError) {
        // 如果是取消操作，直接返回
        if (downloadError.name === 'AbortError' || downloadError.message === 'canceled') {
          console.log('下载已取消')
          return
        }
        console.error('文件下载失败:', downloadError)
        loadProgress.value = 0
        loadStatus.value = '下载失败'
        isLoading = false
        return
      } finally {
        currentLoadController = null
      }

      // 检查文件大小限制
      const MAX_ZIP_SIZE = 1024 * 1024 * 1024 // 1GB限制
      if (zipBlob.size > MAX_ZIP_SIZE) {
        console.error(`ZIP文件过大（${formatFileSize(zipBlob.size)}），无法加载。请使用小于1GB的文件。`)
        loadStatus.value = '文件过大'
        loadProgress.value = 0
        isLoading = false
        return
      }

      try {
        const zip = new JSZip()
        console.log('开始解压ZIP文件...')
        zipContent = await zip.loadAsync(zipBlob)
        console.log('ZIP解压成功，文件数量:', Object.keys(zipContent.files).length)

        loadProgress.value = 50
        loadStatus.value = '解压完成，开始加载模型...'

        // 保存到 IndexedDB 缓存
        try {
          await saveModelToIndexedDB(localStorageKey, zipBlob, row)
          // 清理旧缓存
          await cleanupIndexedDBCache(5)
        } catch (cacheError) {
          console.warn('保存到 IndexedDB 缓存失败:', cacheError)
          // 缓存失败不影响主要功能
        }
      } catch (error) {
        console.error('解压ZIP文件失败:', error)
        loadProgress.value = 0
        loadStatus.value = '解压失败'
        isLoading = false
        return
      }
    }

    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('加载已取消，跳过模型加载')
      return
    }

    // 根据文件类型选择加载方式
    if (fileType === 'gltf' || fileType === 'glb') {
      loadStatus.value = '加载GLTF模型...'
      await loadGLTFModel(zipContent, fileType, loadId)
    } else if (fileType === 'ply') {
      loadStatus.value = '加载PLY模型...'
      await loadPLYModel(zipContent, loadId)
    } else {
      loadStatus.value = '加载OBJ模型...'
      await loadOBJModel(zipContent, loadId)
    }

    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('加载已取消，跳过完成处理')
      return
    }

    // 加载完成
    loadProgress.value = 100
    loadStatus.value = '加载完成'
    isLoading = false

    console.log('模型加载完成')
  } catch (error) {
    // 如果是取消操作，不显示错误
    if (error.name === 'AbortError' || error.message === 'canceled') {
      console.log('模型加载被取消')
      return
    }
    console.error('加载模型失败:', error)
    loadProgress.value = 0
    loadStatus.value = '加载失败'
    isLoading = false
  }
}

/**
 * 从 URL 直接加载 TIFF 文件
 */
async function loadTIFFFromUrl (url, loadId, row) {
  try {
    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('TIFF加载已取消，跳过:', loadId)
      return
    }

    loadStatus.value = '下载TIFF文件...'
    loadProgress.value = 10

    // 创建AbortController用于取消下载
    currentLoadController = new AbortController()

    // 下载TIFF文件为ArrayBuffer
    let arrayBuffer
    try {
      const response = await axios({
        url: url,
        method: 'get',
        responseType: 'arraybuffer',
        timeout: 600000, // 10分钟超时
        signal: currentLoadController.signal,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            loadProgress.value = Math.max(loadProgress.value, Math.min(percentCompleted, 90))
          }
        }
      })

      arrayBuffer = response.data
      console.log('TIFF文件下载完成，大小:', formatFileSize(arrayBuffer.byteLength))

      // 检查是否是当前加载
      if (loadId !== currentLoadId) {
        console.log('下载后检查，TIFF加载已取消')
        return
      }
    } catch (downloadError) {
      // 如果是取消操作，直接返回
      if (downloadError.name === 'AbortError' || downloadError.message === 'canceled') {
        console.log('TIFF下载已取消')
        return
      }
      console.error('下载TIFF文件失败:', downloadError)
      loadProgress.value = 0
      loadStatus.value = '下载失败'
      isLoading = false
      return
    } finally {
      currentLoadController = null
    }

    // 检查文件大小限制
    const MAX_TIFF_SIZE = 1024 * 1024 * 500 // 500MB限制
    if (arrayBuffer.byteLength > MAX_TIFF_SIZE) {
      console.error(`TIFF文件过大（${formatFileSize(arrayBuffer.byteLength)}），无法加载。请使用小于500MB的文件。`)
      loadStatus.value = '文件过大'
      loadProgress.value = 0
      isLoading = false
      return
    }

    // 解析TIFF文件
    await parseAndDisplayTIFF(arrayBuffer, loadId)

    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('TIFF解析后检查，加载已取消')
      return
    }

    // 加载完成
    loadProgress.value = 100
    loadStatus.value = '加载完成'
    isLoading = false

    console.log('TIFF文件加载完成')
  } catch (error) {
    // 如果是取消操作，不显示错误
    if (error.name === 'AbortError' || error.message === 'canceled') {
      console.log('TIFF加载被取消')
      return
    }
    console.error('加载TIFF文件失败:', error)
    loadProgress.value = 0
    loadStatus.value = '加载失败'
    isLoading = false
  }
}

/**
 * 解析并显示 TIFF 数据
 */
async function parseAndDisplayTIFF (arrayBuffer, loadId) {
  try {
    loadStatus.value = '解析TIFF图像...'
    loadProgress.value = 95

    // 解析TIFF文件
    const tiff = await fromArrayBuffer(arrayBuffer)
    const image = await tiff.getImage()

    // 获取图像参数
    const width = image.getWidth()
    const height = image.getHeight()
    const samplesPerPixel = image.getSamplesPerPixel()
    const bitsPerSample = image.getBitsPerSample()

    console.log(`TIFF图像信息: ${width}x${height}, 通道数: ${samplesPerPixel}, 位深: ${bitsPerSample}`)

    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('TIFF解析中检查，加载已取消')
      return
    }

    loadStatus.value = '读取TIFF数据...'

    // 读取栅格数据
    let raster
    try {
      raster = await image.readRasters()
    } catch (error) {
      console.error('读取TIFF栅格数据失败:', error)
      raster = await readTIFFInChunks(image)
    }

    // 检查是否是当前加载
    if (loadId !== currentLoadId) {
      console.log('TIFF数据读取后检查，加载已取消')
      return
    }

    loadStatus.value = '渲染TIFF图像...'

    // 根据通道数处理图像数据
    let imageData
    if (samplesPerPixel === 1) {
      imageData = createImageFromGrayData(raster[0], width, height)
    } else if (samplesPerPixel === 3) {
      imageData = createImageFromRGBData(raster[0], raster[1], raster[2], width, height)
    } else if (samplesPerPixel === 4) {
      imageData = createImageFromRGBAData(raster[0], raster[1], raster[2], raster[3], width, height)
    } else {
      console.warn(`多光谱图像(${samplesPerPixel}个通道)，使用前3个通道`)
      imageData = createImageFromRGBData(raster[0], raster[1], raster[2], width, height)
    }

    // 创建Three.js纹理
    const texture = new THREE.CanvasTexture(imageData)
    texture.needsUpdate = true
    texture.flipY = false // TIFF图像通常不需要翻转

    // 设置纹理参数以提高渲染质量
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping

    // 如果图像有透明度，启用alpha
    if (samplesPerPixel === 4) {
      texture.format = THREE.RGBAFormat
    }

    // 根据图像尺寸创建平面 - 关键修改：增大平面尺寸
    // 计算合适的平面尺寸，使图像清晰可见
    const maxDimension = Math.max(width, height)
    const scaleFactor = 1.0 // 调整为1.0，增大平面尺寸

    // 创建平面几何体，保持图像宽高比
    const aspectRatio = width / height
    let planeWidth, planeHeight

    if (aspectRatio > 1) {
      // 宽图
      planeWidth = 10 * scaleFactor
      planeHeight = 10 / aspectRatio * scaleFactor
    } else {
      // 高图
      planeWidth = 10 * aspectRatio * scaleFactor
      planeHeight = 10 * scaleFactor
    }

    const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight, 1, 1)

    // 根据图像通道数设置材质
    let planeMaterial
    if (samplesPerPixel === 4) {
      // 对于RGBA图像，使用透明材质
      planeMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1.0,
        depthWrite: false // 禁用深度写入以避免渲染问题
      })
    } else {
      // 对于RGB或灰度图像，使用不透明材质
      planeMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: false
      })
    }

    const plane = new THREE.Mesh(planeGeometry, planeMaterial)
    plane.position.set(0, 0, 0)

    // 给平面添加标识
    plane.userData = {
      id: `tiff_${Date.now()}`,
      loadId: loadId,
      type: 'tiff',
      width: width,
      height: height,
      samplesPerPixel: samplesPerPixel
    }

    // 添加到场景
    scene.add(plane)
    model = plane

    // 更新渲染器尺寸以确保正确渲染
    updateRendererSize()

    // 调整相机以适应平面 - 调用优化后的函数
    // fitPlaneToViewTIFF(plane, planeWidth, planeHeight)
    adjustCameraForTIFF(plane)

    // 添加调试信息
    console.log('TIFF图像渲染完成，平面尺寸:', {
      width: planeWidth,
      height: planeHeight,
      aspectRatio: aspectRatio,
      imageSize: { width, height }
    })

    // 强制渲染一次，确保图像显示
    renderer.render(scene, camera)
  } catch (error) {
    console.error('解析和显示TIFF失败:', error)
    throw error
  }
}

function adjustCameraForTIFF (plane) {
  if (!plane || !camera || !controls) return

  // 禁用控制器临时
  controls.enabled = false

  // 计算平面的边界框
  const bbox = new THREE.Box3().setFromObject(plane)
  const center = bbox.getCenter(new THREE.Vector3())
  const size = bbox.getSize(new THREE.Vector3())

  // 计算相机距离 - 增加距离确保能看到整个平面
  const maxDim = Math.max(size.x, size.y, size.z)
  const distance = maxDim * 2

  // 设置相机位置 - 从上方45度角观察
  camera.position.set(
    center.x + distance,
    center.y + distance,
    center.z + distance
  )

  // 看向平面中心
  camera.lookAt(center)

  // 更新控制器
  controls.target.copy(center)
  controls.update()

  // 重新启用控制器
  controls.enabled = true

  // 设置控制器限制
  controls.minDistance = maxDim * 0.1
  controls.maxDistance = maxDim * 10

  console.log('TIFF相机调整:', {
    center: center,
    size: size,
    distance: distance,
    cameraPosition: camera.position,
    controlsTarget: controls.target
  })
}

/**
 * 专门为TIFF图像调整相机视角
 */
function fitPlaneToViewTIFF (plane, planeWidth, planeHeight) {
  if (!plane || !camera || !controls) return

  // 禁用控制器临时
  controls.enabled = false

  // 计算平面边界框
  const bbox = new THREE.Box3().setFromObject(plane)
  const center = bbox.getCenter(new THREE.Vector3())
  const size = bbox.getSize(new THREE.Vector3())

  // 计算平面对角线长度，用于确定相机距离
  const diagonal = Math.sqrt(planeWidth * planeWidth + planeHeight * planeHeight)

  // 计算相机距离，确保整个平面在视野中
  const fov = camera.fov * (Math.PI / 180)
  const distance = diagonal / (2 * Math.tan(fov / 2)) * 1.2

  // 设置相机位置 - 从正上方（y轴）看向平面
  camera.position.set(0, distance, 0)
  camera.lookAt(center)

  // 更新控制器
  controls.target.copy(center)
  controls.update()
  controls.enabled = true

  // 设置控制器限制
  const maxDimension = Math.max(size.x, size.y, size.z)
  controls.minDistance = maxDimension * 0.1
  controls.maxDistance = maxDimension * 10

  console.log('相机调整完成:', {
    center,
    size,
    diagonal,
    distance,
    cameraPos: camera.position,
    controlsTarget: controls.target
  })
}

/**
 * 分块读取大TIFF文件（处理内存限制）
 */
async function readTIFFInChunks (image) {
  const width = image.getWidth()
  const height = image.getHeight()
  const samplesPerPixel = image.getSamplesPerPixel()

  // 如果图像太大，分块读取
  const CHUNK_SIZE = 1000 // 每块的行数
  const chunks = Math.ceil(height / CHUNK_SIZE)

  const rasterArrays = []
  for (let i = 0; i < samplesPerPixel; i++) {
    rasterArrays.push(new Uint8Array(width * height))
  }

  for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
    const startRow = chunkIndex * CHUNK_SIZE
    const endRow = Math.min(startRow + CHUNK_SIZE, height)
    const chunkHeight = endRow - startRow

    // 更新进度
    loadProgress.value = 95 + Math.round((chunkIndex / chunks) * 4)
    loadStatus.value = `读取TIFF数据 (${chunkIndex + 1}/${chunks})...`

    try {
      const chunkRaster = await image.readRasters({
        window: [0, startRow, width, endRow]
      })

      // 将块数据复制到完整数组中
      for (let band = 0; band < samplesPerPixel; band++) {
        const destStart = startRow * width
        const chunkData = chunkRaster[band]

        if (chunkData) {
          rasterArrays[band].set(chunkData, destStart)
        }
      }
    } catch (error) {
      console.warn(`读取TIFF块 ${chunkIndex} 失败:`, error)
      // 继续处理下一个块
    }
  }

  return rasterArrays
}

/**
 * 从灰度数据创建图像
 */
function createImageFromGrayData (grayData, width, height) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  const imageData = ctx.createImageData(width, height)

  // 优化性能：使用TypedArray操作
  const data = imageData.data

  for (let i = 0; i < grayData.length; i++) {
    const grayValue = grayData[i]
    const idx = i * 4

    data[idx] = grayValue // R
    data[idx + 1] = grayValue // G
    data[idx + 2] = grayValue // B
    data[idx + 3] = 255 // A
  }

  ctx.putImageData(imageData, 0, 0)
  return canvas
}

/**
 * 从RGB数据创建图像
 */
function createImageFromRGBData (rData, gData, bData, width, height) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data

  for (let i = 0; i < rData.length; i++) {
    const idx = i * 4

    data[idx] = rData[i] // R
    data[idx + 1] = gData[i] // G
    data[idx + 2] = bData[i] // B
    data[idx + 3] = 255 // A
  }

  ctx.putImageData(imageData, 0, 0)
  return canvas
}

/**
 * 从RGBA数据创建图像
 */
function createImageFromRGBAData (rData, gData, bData, aData, width, height) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  const imageData = ctx.createImageData(width, height)
  const data = imageData.data

  for (let i = 0; i < rData.length; i++) {
    const idx = i * 4

    data[idx] = rData[i] // R
    data[idx + 1] = gData[i] // G
    data[idx + 2] = bData[i] // B
    data[idx + 3] = aData ? aData[i] : 255 // A
  }

  ctx.putImageData(imageData, 0, 0)
  return canvas
}

/**
 * 调整相机以适应平面
 */
function fitPlaneToView (plane, width, height) {
  if (!plane || !camera || !controls) return

  // 禁用控制器临时
  controls.enabled = false

  // 计算平面大小
  const planeSize = new THREE.Box3().setFromObject(plane).getSize(new THREE.Vector3())
  const maxSize = Math.max(planeSize.x, planeSize.y, planeSize.z)
  const center = new THREE.Box3().setFromObject(plane).getCenter(new THREE.Vector3())

  // 计算相机距离
  const fov = camera.fov * (Math.PI / 180)
  const aspect = camera.aspect
  const distance = Math.max(planeSize.x, planeSize.y) / (2 * Math.tan(fov / 2)) * 1.5

  // 设置相机位置
  camera.position.set(
    center.x,
    center.y,
    center.z + Math.max(distance, maxSize * 2)
  )
  camera.lookAt(center)

  // 更新控制器
  controls.target.copy(center)
  controls.update()
  controls.enabled = true

  // 设置控制器限制
  controls.minDistance = maxSize * 0.1
  controls.maxDistance = maxSize * 10
}

function loadPLYModel (zipContent, loadId) {
  return new Promise((resolve, reject) => {
    const loadModel = async () => {
      try {
        // 检查是否是当前加载
        if (loadId !== currentLoadId) {
          reject(new Error('canceled'))
          return
        }
        let plyFile = null
        zipContent.forEach((relativePath, zipEntry) => {
          if (!zipEntry.dir) {
            const fileName = relativePath.toLowerCase()
            if (fileName.endsWith('.ply')) {
              plyFile = zipEntry
            }
          }
        })

        if (!plyFile) {
          reject(new Error('ZIP文件中未找到ply文件'))
          return
        }
        const blob = await plyFile.async('blob')

        const arrayBuffer = await blob.arrayBuffer()

        // 创建PLY加载器
        const plyLoader = new PLYLoader()
        const geometry = plyLoader.parse(arrayBuffer)
        if (geometry.attributes.color) {
          const colorAttr = geometry.attributes.color
          const array = colorAttr.array
          let max = 0
          for (let i = 0; i < array.length; i++) {
            if (array[i] > max) max = array[i]
          }
          if (max > 1.0) {
            console.log(`归一化顶点颜色：最大值 ${max}，除以 255`)
            for (let i = 0; i < array.length; i++) {
              array[i] /= 255
            }
          } else {
            console.log('顶点颜色已在0-1范围')
          }
          // 确保颜色属性被标记为需要
          geometry.attributes.color.needsUpdate = true
        }
        loadStatus.value = '生成高斯模型...'
        loadProgress.value = 80
        geometry.computeBoundingBox()
        geometry.computeVertexNormals()
        geometry.computeBoundingSphere()
        geometry.center()
        // 创建材质和模型（同原代码，略）
        const isPointCloud = !geometry.index
        const hasColors = geometry.hasAttribute('color')
        console.log('模型加载完成，模型：', geometry)
        // 调试：打印颜色信息
        if (hasColors) {
          console.log('模型包含顶点颜色')
        } else {
          console.log('模型无顶点颜色，将使用默认材质颜色')
        }
        // 创建材质
        let material
        if (isPointCloud) {
          material = new THREE.PointsMaterial({
            size: geometry.boundingSphere.radius * 0.01, // 自适应点大小
            vertexColors: hasColors,
            color: hasColors ? undefined : 0xcccccc, // 若无颜色则用灰色
            sizeAttenuation: true
          })
        } else {
          material = new THREE.MeshStandardMaterial({
            color: hasColors ? undefined : 0xcccccc,
            vertexColors: hasColors,
            flatShading: false, // 平滑着色
            side: THREE.DoubleSide,
            roughness: 0.5,
            metalness: 0.1,
            emissive: 0x000000
          })
        }
        // 移除旧模型
        if (model) {
          disposeObject(model)
          scene.remove(model)
        }

        model = isPointCloud
          ? new THREE.Points(geometry, material)
          : new THREE.Mesh(geometry, material)

        scene.add(model)
        // 环境光增强
        scene.add(new THREE.AmbientLight(0x404060, 2.0)) // 强度提高至2.0

        // 主方向光强度降低
        const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8)
        dirLight1.position.set(2, 5, 5)
        scene.add(dirLight1)

        // 背光强度降低
        const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.5)
        dirLight2.position.set(-3, 1, -4)
        scene.add(dirLight2)

        // 侧光强度降低
        const dirLight3 = new THREE.DirectionalLight(0xffffff, 0.4)
        dirLight3.position.set(-3, 2, 3)
        scene.add(dirLight3)
        // 调整相机
        const radius = geometry.boundingSphere.radius
        camera.position.set(0, 0, radius * 2.5)
        controls.target.set(0, 0, 0)
        controls.update()

        loadProgress.value = 100
        loadStatus.value = '加载完成'
      } catch (error) {
        // 检查是否是当前加载
        if (loadId !== currentLoadId) {
          console.log('PLY加载错误：加载已取消')
          reject(new Error('canceled'))
          return
        }
        console.error('加载PLY失败:', error)
        reject(error)
      }
    }
    // 立即调用异步函数
    loadModel()
  })
}

/** 加载GLTF/GLB模型 */
function loadGLTFModel (zipContent, fileType, loadId) {
  return new Promise((resolve, reject) => {
    // 将异步操作提取到内部函数
    const loadModel = async () => {
      try {
        // 检查是否是当前加载
        if (loadId !== currentLoadId) {
          reject(new Error('canceled'))
          return
        }

        // 查找GLTF或GLB文件
        let gltfFile = null
        let gltfFileName = null

        zipContent.forEach((relativePath, zipEntry) => {
          if (!zipEntry.dir) {
            const fileName = relativePath.toLowerCase()
            if (fileType === 'glb' && fileName.endsWith('.glb')) {
              gltfFile = zipEntry
              gltfFileName = relativePath
            } else if (fileType === 'gltf' && fileName.endsWith('.gltf')) {
              gltfFile = zipEntry
              gltfFileName = relativePath
            }
          }
        })

        if (!gltfFile) {
          reject(new Error(`ZIP文件中未找到${fileType.toUpperCase()}文件`))
          return
        }

        // 创建GLTF加载器
        const gltfLoader = new GLTFLoader()

        // 如果是GLB格式（二进制）
        if (fileType === 'glb') {
          const glbBlob = await gltfFile.async('blob')
          const glbUrl = URL.createObjectURL(glbBlob)

          // 保存URL以便清理
          blobUrls.push(glbUrl)

          // 加载GLB
          gltfLoader.load(
            glbUrl,
            (gltf) => {
              // 检查是否是当前加载
              if (loadId !== currentLoadId) {
                console.log('GLB加载回调：加载已取消，跳过')
                reject(new Error('canceled'))
                return
              }

              // 处理加载的模型
              const scene = gltf.scene || gltf.scenes[0]

              // 如果有动画，播放第一个动画
              if (gltf.animations && gltf.animations.length > 0) {
                const mixer = new THREE.AnimationMixer(scene)
                gltf.animations.forEach((clip) => {
                  mixer.clipAction(clip).play()
                })
                // 保存mixer以便后续更新动画
                window.animationMixer = mixer
              }

              // 添加到场景
              onModelLoaded(gltf, loadId)
              resolve()
            },
            (progress) => {
              // 检查是否是当前加载
              if (loadId !== currentLoadId) {
                console.log('GLB加载进度：加载已取消')
                return
              }

              // 加载进度
              if (progress.lengthComputable) {
                const percentComplete = (progress.loaded / progress.total) * 100
                const totalProgress = 50 + Math.round(percentComplete * 0.4) // 从50%到90%
                loadProgress.value = Math.max(loadProgress.value, totalProgress)
                loadStatus.value = '加载模型数据...'
              }
            },
            (error) => {
              // 检查是否是当前加载
              if (loadId !== currentLoadId) {
                console.log('GLB加载错误：加载已取消')
                reject(new Error('canceled'))
                return
              }
              console.error('加载GLB失败:', error)
              reject(error)
            }
          )
        } else {
          // GLTF格式（JSON，可能需要额外资源）
          // 检查GLTF文件大小
          const MAX_TEXT_SIZE = 1024 * 1024 * 1024 // 500MB限制
          let gltfFileSize = 0
          try {
            if (gltfFile._data && gltfFile._data.uncompressedSize) {
              gltfFileSize = gltfFile._data.uncompressedSize
            } else if (gltfFile.uncompressedSize) {
              gltfFileSize = gltfFile.uncompressedSize
            }
          } catch (e) {
            // 无法获取文件大小，继续尝试加载
          }

          if (gltfFileSize > 0 && gltfFileSize > MAX_TEXT_SIZE) {
            reject(new Error(`GLTF文件过大（${formatFileSize(gltfFileSize)}），无法加载。请使用小于500MB的文件。`))
            return
          }

          let gltfText
          try {
            gltfText = await gltfFile.async('text')
          } catch (error) {
            console.error('读取GLTF文件失败:', error)
            if (error.message && error.message.includes('string length')) {
              reject(new Error('GLTF文件过大，超出了浏览器字符串长度限制。请使用较小的文件或使用GLB格式。'))
            } else {
              reject(new Error('读取GLTF文件失败: ' + (error.message || error)))
            }
            return
          }

          let gltfData
          try {
            gltfData = JSON.parse(gltfText)
          } catch (error) {
            reject(new Error('解析GLTF JSON失败: ' + (error.message || error)))
            return
          }

          // 创建ZIP文件映射
          const zipFiles = {}
          zipContent.forEach((relativePath, zipEntry) => {
            if (!zipEntry.dir) {
              zipFiles[relativePath] = zipEntry
              zipFiles[relativePath.toLowerCase()] = zipEntry
              const fileName = relativePath.split('/').pop() || relativePath.split('\\').pop() || relativePath
              zipFiles[fileName] = zipEntry
              zipFiles[fileName.toLowerCase()] = zipEntry
            }
          })

          // 创建文件映射，预先解析所有资源URL
          const fileUrlMap = {}
          for (const [key, zipEntry] of Object.entries(zipFiles)) {
            if (zipEntry && typeof zipEntry.async === 'function') {
              try {
                const blob = await zipEntry.async('blob')
                const blobUrl = URL.createObjectURL(blob)
                blobUrls.push(blobUrl)
                fileUrlMap[key] = blobUrl
              } catch (e) {
                console.warn('加载ZIP文件失败:', key, e)
              }
            }
          }

          // 创建自定义资源解析函数
          const resolveURL = (url) => {
            let resolvedUrl = fileUrlMap[url]
            if (!resolvedUrl) {
              resolvedUrl = fileUrlMap[url.toLowerCase()]
            }
            if (!resolvedUrl) {
              const fileName = url.split('/').pop() || url.split('\\').pop() || url
              resolvedUrl = fileUrlMap[fileName] || fileUrlMap[fileName.toLowerCase()]
            }
            if (!resolvedUrl) {
              try {
                const decodedUrl = decodeURIComponent(url)
                resolvedUrl = fileUrlMap[decodedUrl] || fileUrlMap[decodedUrl.toLowerCase()]
                if (!resolvedUrl) {
                  const decodedFileName = decodedUrl.split('/').pop() || decodedUrl.split('\\').pop() || decodedUrl
                  resolvedUrl = fileUrlMap[decodedFileName] || fileUrlMap[decodedFileName.toLowerCase()]
                }
              } catch (e) {
                // 忽略解码错误
              }
            }

            return resolvedUrl || url
          }

          // 创建自定义加载器
          const originalTextureLoaderLoad = THREE.TextureLoader.prototype.load
          const originalFileLoaderLoad = THREE.FileLoader.prototype.load

          THREE.TextureLoader.prototype.load = function (url, onLoad, onProgress, onError) {
            const resolvedUrl = resolveURL(url)
            originalTextureLoaderLoad.call(this, resolvedUrl, onLoad, onProgress, onError)
          }

          THREE.FileLoader.prototype.load = function (url, onLoad, onProgress, onError) {
            const resolvedUrl = resolveURL(url)
            originalFileLoaderLoad.call(this, resolvedUrl, onLoad, onProgress, onError)
          }

          // 创建GLTF JSON的Blob URL
          const gltfBlob = new Blob([JSON.stringify(gltfData)], { type: 'application/json' })
          const gltfUrl = URL.createObjectURL(gltfBlob)
          blobUrls.push(gltfUrl)

          // 加载GLTF
          gltfLoader.load(
            gltfUrl,
            async (gltf) => {
              // 检查是否是当前加载
              if (loadId !== currentLoadId) {
                THREE.TextureLoader.prototype.load = originalTextureLoaderLoad
                THREE.FileLoader.prototype.load = originalFileLoaderLoad
                console.log('GLTF加载回调：加载已取消，跳过')
                reject(new Error('canceled'))
                return
              }

              // 恢复原始加载方法
              THREE.TextureLoader.prototype.load = originalTextureLoaderLoad
              THREE.FileLoader.prototype.load = originalFileLoaderLoad

              // 处理加载的模型
              const scene = gltf.scene || gltf.scenes[0]

              // 优化材质和纹理
              scene.traverse((child) => {
                if (child.isMesh) {
                  if (child.material) {
                    if (Array.isArray(child.material)) {
                      child.material.forEach(mat => {
                        if (mat) {
                          mat.needsUpdate = true
                          mat.toneMapped = true
                          // 处理纹理
                          const arr = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap']
                          arr.forEach(texType => {
                            if (mat[texType]) {
                              mat[texType].needsUpdate = true
                              mat[texType].flipY = false
                              if (texType === 'map' || texType === 'emissiveMap') {
                                if (mat[texType].colorSpace !== undefined) {
                                  mat[texType].colorSpace = THREE.SRGBColorSpace
                                } else if (mat[texType].encoding !== undefined) {
                                  mat[texType].encoding = THREE.sRGBEncoding
                                }
                              }
                            }
                          })
                        }
                      })
                    } else {
                      child.material.needsUpdate = true
                      child.material.toneMapped = true
                      // 处理纹理
                      const arr = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap']
                      arr.forEach(texType => {
                        if (child.material[texType]) {
                          child.material[texType].needsUpdate = true
                          child.material[texType].flipY = false
                          if (texType === 'map' || texType === 'emissiveMap') {
                            if (child.material[texType].colorSpace !== undefined) {
                              child.material[texType].colorSpace = THREE.SRGBColorSpace
                            } else if (child.material[texType].encoding !== undefined) {
                              child.material[texType].encoding = THREE.sRGBEncoding
                            }
                          }
                        }
                      })
                    }
                  }
                }
              })

              // 如果有动画，播放第一个动画
              if (gltf.animations && gltf.animations.length > 0) {
                const mixer = new THREE.AnimationMixer(scene)
                gltf.animations.forEach((clip) => {
                  mixer.clipAction(clip).play()
                })
                window.animationMixer = mixer
              }

              onModelLoaded(gltf, loadId)
              resolve()
            },
            (progress) => {
              // 检查是否是当前加载
              if (loadId !== currentLoadId) {
                console.log('GLTF加载进度：加载已取消')
                return
              }

              // 加载进度
              if (progress.lengthComputable) {
                const percentComplete = (progress.loaded / progress.total) * 100
                const totalProgress = 50 + Math.round(percentComplete * 0.4) // 从50%到90%
                loadProgress.value = Math.max(loadProgress.value, totalProgress)
                loadStatus.value = '加载模型数据...'
              }
            },
            (error) => {
              THREE.TextureLoader.prototype.load = originalTextureLoaderLoad
              THREE.FileLoader.prototype.load = originalFileLoaderLoad

              // 检查是否是当前加载
              if (loadId !== currentLoadId) {
                console.log('GLTF加载错误：加载已取消')
                reject(new Error('canceled'))
                return
              }

              console.error('加载GLTF失败:', error)
              reject(error)
            }
          )
        }
      } catch (error) {
        console.error('加载GLTF/GLB失败:', error)
        reject(error)
      }
    }

    // 立即调用异步函数
    loadModel()
  })
}

/** 加载OBJ模型 */
function loadOBJModel (zipContent, loadId) {
  return new Promise((resolve, reject) => {
    // 将异步操作提取到内部函数
    const loadModel = async () => {
      try {
        // 检查是否是当前加载
        if (loadId !== currentLoadId) {
          reject(new Error('canceled'))
          return
        }

        // 查找OBJ和MTL文件
        let objFile = null
        let mtlFile = null
        let objFileName = null
        let mtlFileName = null

        zipContent.forEach((relativePath, zipEntry) => {
          if (!zipEntry.dir) {
            const fileName = relativePath.toLowerCase()
            if (fileName.endsWith('.obj')) {
              objFile = zipEntry
              objFileName = relativePath
            } else if (fileName.endsWith('.mtl')) {
              mtlFile = zipEntry
              mtlFileName = relativePath
            }
          }
        })

        if (!objFile) {
          reject(new Error('ZIP文件中未找到OBJ文件'))
          return
        }

        // 检查文件大小，避免超出字符串长度限制
        const MAX_TEXT_SIZE = 1024 * 1024 * 1024 // 500MB限制
        let objFileSize = 0
        try {
          if (objFile._data && objFile._data.uncompressedSize) {
            objFileSize = objFile._data.uncompressedSize
          } else if (objFile.uncompressedSize) {
            objFileSize = objFile.uncompressedSize
          }
        } catch (e) {
          // 无法获取文件大小，继续尝试加载
        }

        if (objFileSize > 0 && objFileSize > MAX_TEXT_SIZE) {
          reject(new Error(`OBJ文件过大（${formatFileSize(objFileSize)}），无法加载。请使用小于500MB的文件。`))
          return
        }

        // 读取OBJ文件内容
        let objContent
        try {
          objContent = await objFile.async('text')
        } catch (error) {
          console.error('读取OBJ文件失败:', error)
          if (error.message && error.message.includes('string length')) {
            reject(new Error('OBJ文件过大，超出了浏览器字符串长度限制。请使用较小的文件或使用GLTF/GLB格式。'))
          } else {
            reject(new Error('读取OBJ文件失败: ' + (error.message || error)))
          }
          return
        }

        // 导入OBJ和MTL加载器
        const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js')
        const objLoader = new OBJLoader()

        // 如果有MTL文件，先加载材质
        if (mtlFile) {
          const { MTLLoader } = await import('three/examples/jsm/loaders/MTLLoader.js')

          let mtlFileSize = 0
          try {
            if (mtlFile._data && mtlFile._data.uncompressedSize) {
              mtlFileSize = mtlFile._data.uncompressedSize
            } else if (mtlFile.uncompressedSize) {
              mtlFileSize = mtlFile.uncompressedSize
            }
          } catch (e) {
            // 无法获取文件大小，继续尝试加载
          }

          if (mtlFileSize > 0 && mtlFileSize > MAX_TEXT_SIZE) {
            reject(new Error(`MTL文件过大（${formatFileSize(mtlFileSize)}），无法加载。`))
            return
          }

          let mtlContent
          try {
            mtlContent = await mtlFile.async('text')
          } catch (error) {
            console.error('读取MTL文件失败:', error)
            reject(new Error('读取MTL文件失败: ' + (error.message || error)))
            return
          }

          // 加载MTL和OBJ
          await loadMTLAndOBJ(mtlContent, objContent, zipContent, loadId)
        } else {
          // 没有MTL文件，直接加载OBJ
          await loadOBJOnly(objContent, loadId)
        }

        resolve()
      } catch (error) {
        console.error('加载OBJ模型失败:', error)
        reject(error)
      }
    }

    // 立即调用异步函数
    loadModel()
  })
}

/** 加载MTL和OBJ */
async function loadMTLAndOBJ (mtlContent, objContent, zipContent, loadId) {
  const { MTLLoader } = await import('three/examples/jsm/loaders/MTLLoader.js')
  const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js')

  return new Promise((resolve, reject) => {
    // 将异步操作提取到内部函数
    const load = async () => {
      try {
        // 检查是否是当前加载
        if (loadId !== currentLoadId) {
          reject(new Error('canceled'))
          return
        }

        // 创建MTL加载器
        const mtlLoader = new MTLLoader()

        // 创建自定义纹理加载器
        const textureLoader = new THREE.TextureLoader()
        const originalLoad = textureLoader.load.bind(textureLoader)

        // 重写TextureLoader的load方法，从ZIP中加载纹理
        textureLoader.load = (url, onLoad, onProgress, onError) => {
          const isTextureFile = !url.includes('://') && !url.includes('/') && !url.includes('\\')

          if (isTextureFile) {
            // 在ZIP中查找纹理文件
            let textureFile = null
            const textureName = url.toLowerCase()

            zipContent.forEach((relativePath, zipEntry) => {
              if (!zipEntry.dir) {
                const fileName = relativePath.toLowerCase()
                const baseName = fileName.replace(/\.(jpg|jpeg|png|gif|bmp|tga|tiff)$/, '')
                const textureBaseName = textureName.replace(/\.(jpg|jpeg|png|gif|bmp|tga|tiff)$/, '')

                if (baseName === textureBaseName || fileName.endsWith(textureName)) {
                  textureFile = zipEntry
                  return false
                }
              }
            })

            if (textureFile) {
              textureFile.async('blob').then(textureBlob => {
                const textureUrl = URL.createObjectURL(textureBlob)
                blobUrls.push(textureUrl)

                originalLoad(textureUrl, onLoad, onProgress, onError)
              }).catch(error => {
                console.error('从ZIP加载纹理失败:', error)
                if (onError) onError(error)
                else if (onLoad) onLoad(null)
              })
            } else {
              console.warn('未找到纹理文件:', url)
              if (onLoad) onLoad(null)
            }
          } else {
            originalLoad(url, onLoad, onProgress, onError)
          }
        }

        if (typeof mtlLoader.setTextureLoader === 'function') {
          mtlLoader.setTextureLoader(textureLoader)
        }

        const materials = mtlLoader.parse(mtlContent)
        if (materials && materials.textureLoader) {
          materials.textureLoader = textureLoader
        }

        materials.preload()

        // 等待材质预加载完成
        await new Promise((resolve) => {
          setTimeout(() => {
            resolve()
          }, 100)
        })

        const objLoader = new OBJLoader()
        objLoader.setMaterials(materials)

        const object = objLoader.parse(objContent)

        // 调整OBJ材质亮度
        object.traverse((child) => {
          if (child.isMesh && child.material) {
            const materials = Array.isArray(child.material) ? child.material : [child.material]
            materials.forEach(mat => {
              if (mat && mat.color) {
                mat.color.multiplyScalar(0.85)
                mat.needsUpdate = true
                mat.toneMapped = true
              }
            })
          }
        })

        onModelLoaded({ scene: object }, loadId)
        resolve()
      } catch (error) {
        console.error('加载MTL和OBJ失败:', error)
        reject(error)
      }
    }
    load()
  })
}

/** 加载OBJ（无MTL） */
async function loadOBJOnly (objContent, loadId) {
  const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js')

  return new Promise((resolve, reject) => {
    try {
      // 检查是否是当前加载
      if (loadId !== currentLoadId) {
        reject(new Error('canceled'))
        return
      }

      const objLoader = new OBJLoader()
      const object = objLoader.parse(objContent)

      // 如果没有材质，使用默认材质，并调整亮度
      object.traverse((child) => {
        if (child.isMesh) {
          if (!child.material || !child.material.length) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0xaaaaaa,
              flatShading: true,
              metalness: 0.3,
              roughness: 0.7
            })
          } else {
            const materials = Array.isArray(child.material) ? child.material : [child.material]
            materials.forEach(mat => {
              if (mat && mat.color) {
                mat.color.multiplyScalar(0.85)
                mat.needsUpdate = true
                mat.toneMapped = true
              }
            })
          }
        }
      })

      onModelLoaded({ scene: object }, loadId)
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

// 辅助函数：格式化文件大小
function formatFileSize (bytes) {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/** 下载文件为Blob */
async function downloadFileAsBlob (url, signal) {
  try {
    loadStatus.value = '下载模型中...'

    const response = await axios({
      url: url,
      method: 'get',
      responseType: 'blob',
      timeout: 600000, // 10分钟超时
      signal: signal, // 添加信号支持取消
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          loadProgress.value = Math.max(loadProgress.value, Math.min(percentCompleted, 95))
        }
      }
    })

    if (!response.data) {
      throw new Error('下载的文件为空')
    }

    return response.data
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new Error('canceled')
    }
    console.error('下载文件失败:', error)
    let errorMessage = '下载文件失败'
    if (error.response) {
      errorMessage += `: HTTP ${error.response.status}`
      if (error.response.status === 404) {
        errorMessage = '文件不存在'
      } else if (error.response.status === 413) {
        errorMessage = '文件过大，服务器拒绝下载'
      }
    } else if (error.message) {
      errorMessage += ': ' + error.message
    }
    loadStatus.value = errorMessage
    throw new Error(errorMessage)
  }
}

/**
 * 设置灯光
 */
function setupLights () {
  // 清除现有灯光
  scene.children.forEach(child => {
    if (child.isLight) {
      scene.remove(child)
    }
  })

  // 环境光（基础照明）
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
  scene.add(ambientLight)

  // 主方向光（产生阴影）
  const mainLight = new THREE.DirectionalLight(0xffffff, 0.8)
  mainLight.position.set(10, 20, 10)
  mainLight.castShadow = true
  mainLight.shadow.mapSize.width = 2048
  mainLight.shadow.mapSize.height = 2048
  mainLight.shadow.camera.near = 0.5
  mainLight.shadow.camera.far = 50
  mainLight.shadow.camera.left = -20
  mainLight.shadow.camera.right = 20
  mainLight.shadow.camera.top = 20
  mainLight.shadow.camera.bottom = -20
  scene.add(mainLight)

  // 补光（减少阴影硬度）
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.3)
  fillLight.position.set(-10, 10, -10)
  scene.add(fillLight)

  // 背光（增强轮廓）
  const backLight = new THREE.DirectionalLight(0xffffff, 0.2)
  backLight.position.set(0, 5, -10)
  scene.add(backLight)
}

/**
 * 模型加载完成回调
 */
function onModelLoaded (gltf, loadId) {
  // 检查是否是当前加载
  if (loadId !== currentLoadId) {
    console.log('模型加载完成回调：加载已取消，不添加到场景')
    return
  }

  // 再次检查并清理可能的残留模型
  cleanupSceneModels()

  model = gltf.scene

  // 给模型设置唯一标识，便于后续识别
  model.userData = {
    id: `model_${Date.now()}`,
    loadId: loadId
  }

  // 1. 设置模型属性 - 平放模型
  model.scale.set(1, 1, 1)
  model.position.set(0, 0, 0)
  model.rotation.x = -Math.PI / 2

  // 2. 处理材质和纹理
  model.traverse((child) => {
    if (child.isMesh) {
      // 启用阴影
      child.castShadow = true
      child.receiveShadow = true

      // 3. 处理材质
      if (child.material) {
        // 确保保持原始材质
        const materials = Array.isArray(child.material) ? child.material : [child.material]

        materials.forEach((material, index) => {
          if (material) {
            // 3.1 保存原始材质属性（用于调试）
            material.originalProperties = {
              color: material.color ? material.color.clone() : null,
              map: material.map,
              name: material.name,
              uuid: material.uuid
            }

            // 3.3 确保纹理正确加载
            if (material.map) {
              material.map.needsUpdate = true
              // GLTF/GLB纹理通常不需要翻转
              material.map.flipY = false
              // 设置正确的颜色空间
              if (material.map.colorSpace !== undefined) {
                material.map.colorSpace = THREE.SRGBColorSpace
              } else if (material.map.encoding !== undefined) {
                material.map.encoding = THREE.sRGBEncoding
              }
            }

            // 3.4 处理其他纹理
            const textureTypes = ['normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap']
            textureTypes.forEach(textureType => {
              if (material[textureType]) {
                material[textureType].needsUpdate = true
                material[textureType].flipY = false
              }
            })

            // 3.5 启用色调映射
            material.toneMapped = true
            material.needsUpdate = true

            // 3.7 如果是PBR材质，确保正确设置
            if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
              // 增加亮度显示
              material.emissiveIntensity = 0.1 // 轻微自发光，增强颜色
              material.envMapIntensity = 1.0 // 环境贴图强度

              // 如果是金属材质，降低金属度和粗糙度以增强颜色
              if (material.metalness > 0.5) {
                material.metalness = Math.min(material.metalness, 0.8)
                material.roughness = Math.max(material.roughness, 0.2)
              }
            }
          }
        })
      }
    }
  })

  // 4. 添加到场景
  scene.add(model)

  // 5. 标记加载完成
  loadProgress.value = 100
  loadStatus.value = '加载完成'
  isLoading = false

  // 6. 调整光照设置以增强颜色
  enhanceColorsWithLighting()

  // 7. 自动调整视角

  setTimeout(() => {
    fitToView()
  }, 100)

  console.log('模型加载并添加到场景完成，模型ID:', model.userData.id)
}

/**
 * 增强颜色显示的光照设置
 */
function enhanceColorsWithLighting () {
  // 1. 先清除现有光照
  scene.children.forEach(child => {
    if (child.isLight) {
      scene.remove(child)
    }
  })

  // 2. 创建适合显示颜色的光照
  // 强环境光，确保颜色可见
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.8)
  scene.add(ambientLight)

  // 主方向光 - 使用较柔和的光
  const mainLight = new THREE.DirectionalLight(0xffffff, 0.6)
  mainLight.position.set(10, 20, 10)
  mainLight.castShadow = true
  mainLight.shadow.mapSize.width = 1024
  mainLight.shadow.mapSize.height = 1024
  scene.add(mainLight)

  // 补光 - 从不同角度照亮
  const fillLight1 = new THREE.DirectionalLight(0xffffff, 0.3)
  fillLight1.position.set(-10, 10, -10)
  scene.add(fillLight1)

  const fillLight2 = new THREE.DirectionalLight(0xffffff, 0.2)
  fillLight2.position.set(0, -10, 10)
  scene.add(fillLight2)

  // 背光 - 增强轮廓
  const backLight = new THREE.DirectionalLight(0xffffff, 0.2)
  backLight.position.set(0, 5, -15)
  scene.add(backLight)

  // 3. 半球光 - 提供更自然的照明
  const hemisphereLight = new THREE.HemisphereLight(
    0xffffff, // 天空颜色
    0x888888, // 地面颜色
    0.6 // 强度
  )
  scene.add(hemisphereLight)
}

/**
 * 适应视图 - 极端远距离版本
 */
function fitToView () {
  if (!model) return

  // 禁用控制器临时
  controls.enabled = false

  const bbox = new THREE.Box3().setFromObject(model)
  const center = bbox.getCenter(new THREE.Vector3())

  // 直接设置一个非常大的固定位置
  const targetPosition = new THREE.Vector3(2000, 1500, 2000)

  // 更新相机参数
  camera.near = 1
  camera.far = 10000
  camera.updateProjectionMatrix()

  // 直接设置相机
  camera.position.copy(targetPosition)
  camera.lookAt(center)

  // 重新启用控制器
  controls.target.copy(center)
  controls.update()
  controls.enabled = true

  // 宽松的控制器限制
  controls.minDistance = 1
  controls.maxDistance = 5000
  resetCamera()
}

// 重置相机
const resetCamera = () => {
  if (props.currentModelKey !== 'ZSCGD250829') {
    camera.position.set(25, 50, 10) // 与初始化一致
    controls.target.set(0, 0, 0)
    controls.update()
  } else {
    camera.position.set(0, -30, -15) // 与初始化一致
    controls.target.set(0, 0, 0)
    controls.update()
  }
}

const resetCameraSize = (x, y, z) => {
  camera.position.set(x, y, z) // 与初始化一致
  controls.target.set(0, 0, 0)
  controls.update()
}
/**
 * 清理场景资源
 */
function disposeScene () {
  // 停止动画循环
  if (animationId) {
    cancelAnimationFrame(animationId)
    animationId = null
  }

  // 清理模型
  if (model) {
    scene.remove(model)
    disposeObject(model)
    model = null
  }

  // 清理渲染器
  if (renderer) {
    renderer.dispose()
    renderer = null
  }

  // 清理控制器
  if (controls) {
    controls.dispose()
    controls = null
  }

  // 清理场景
  if (scene) {
    scene.traverse(disposeObject)
    scene = null
  }
}

/**
 * 清理对象资源
 */
function disposeObject (object) {
  if (!object) return

  // 清理几何体
  if (object.geometry) {
    object.geometry.dispose()
    object.geometry = null
  }

  // 清理材质
  if (object.material) {
    if (Array.isArray(object.material)) {
      object.material.forEach(material => {
        if (material) {
          // 清理纹理
          const textureTypes = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'envMap']
          textureTypes.forEach(textureType => {
            if (material[textureType]) {
              material[textureType].dispose()
              material[textureType] = null
            }
          })
          material.dispose()
        }
      })
    } else {
      // 清理纹理
      const textureTypes = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'envMap']
      textureTypes.forEach(textureType => {
        if (object.material[textureType]) {
          object.material[textureType].dispose()
          object.material[textureType] = null
        }
      })
      object.material.dispose()
    }
    object.material = null
  }

  // 清理用户数据
  object.userData = {}

  // 移除所有事件监听器
  object.removeEventListener?.()

  // 清除子对象引用（但不在这里清理子对象，由父级处理）
  object.children = []
}
/**
 * 清理资源
 */
function cleanupResources () {
  // 取消当前加载
  cancelCurrentLoad()

  // 清理Blob URLs
  cleanupBlobUrls()

  // 清理Three.js资源
  disposeScene()

  console.log('所有资源已清理')
}

/**
 * 启动渲染循环
 */
function startRenderLoop () {
  function animate () {
    animationId = requestAnimationFrame(animate)

    // 更新控制器
    controls.update()

    // 更新补间动画
    TWEEN.update()

    // 更新动画混合器
    if (window.animationMixer) {
      window.animationMixer.update(0.016) // 假设60fps
    }

    // 渲染场景
    renderer.render(scene, camera)
  }

  animate()
}

defineExpose({
  loadSelectedModel,
  removeCurrentModel
})
</script>

<style lang="scss" scoped>
.glb-container {
  width: 100%;
  height: 100%;
  position: relative;
}
.fix-camera{
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
  width: 200px;
  background: rgba(255, 255, 255, 0.8);
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  font-size: 14px;
  color: #333;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }
}
.three-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
  // background: rgba(0, 11, 30, 0.95);
  background: rgba(255, 255, 255, 0.95);
}

.three-image {
  position: absolute;
  z-index: 99;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.load-progress {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  min-width: 300px;

  .progress-content {
    background: rgba(255, 255, 255, 0.95);
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(10px);

    .progress-text {
      text-align: center;
      margin-bottom: 16px;
      font-size: 16px;
      color: #333;
      font-weight: 500;
      min-height: 24px;
    }

    .progress-bar-container {
      width: 100%;
      height: 6px;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
      overflow: hidden;
      margin-top: 8px;

      .progress-bar {
        height: 100%;
        background: linear-gradient(90deg, #409EFF, #67C23A);
        border-radius: 3px;
        transition: width 0.3s ease;
      }
    }
  }
}
</style>
